import { defineStore } from "pinia";
import {
  api,
  type Result,
  Success,
  watchRequest,
  type QueryParams,
  type ResultOkRef,
} from "../api";
import { type ZodType } from "zod";
import {
  type OrderAdminPayload,
  type OrderPayload,
} from "../schemas/order/form";
import { OrderType } from "../schemas/order/order";
import {
  OrderMassUpdateResponseSchema,
  type OrderMassUpdatePayload,
  type OrderMassUpdateResponse,
} from "../schemas/order/massUpdate";
import {
  OrderAdminResponseSchema,
  type OrderAdminResponse,
  CheckoutSessionResponseSchema,
  type CheckoutSessionResponse,
  type OrderDeliveryAdminResponse,
  type OrderPickupAdminResponse,
  OrderResponseSchema,
  type OrderDeliveryResponse,
  type OrderPickupResponse,
  type OrderResponse,
} from "../schemas/order/response";
import { PaginatedSchema, type Paginated } from "../schemas/paginated";
import { markRaw } from "vue";

type AnyOrderPayload = OrderPayload | OrderAdminPayload;
type AnyOrderResponse = OrderResponse | OrderAdminResponse;
type OrderFilters = QueryParams & { type?: OrderType };

export type ClientPaginatedOrdersResult = Result<Paginated<OrderResponse[]>>;
export type AdminPaginatedOrdersResult = Result<
  Paginated<OrderAdminResponse[]>
>;
export type PaginatedOrdersResult =
  | ClientPaginatedOrdersResult
  | AdminPaginatedOrdersResult;

class OrderApi<
  TPayload extends AnyOrderPayload & Record<string, unknown>,
  TResponse extends AnyOrderResponse,
  TDelivery extends TResponse,
  TPickup extends TResponse,
> {
  constructor(
    private readonly url: string,
    private readonly responseSchema: ZodType<TResponse>,
  ) {}

  getOrders(
    filters: QueryParams & {
      type: OrderType.DELIVERY;
    },
  ): ResultOkRef<Success<Paginated<TDelivery[]>>>;
  getOrders(
    filters: QueryParams & {
      type: OrderType.PICKUP;
    },
  ): ResultOkRef<Success<Paginated<TPickup[]>>>;
  getOrders(
    filters?: OrderFilters,
  ): ResultOkRef<Success<Paginated<TResponse[]>>>;
  getOrders(
    filters?: OrderFilters,
  ): ResultOkRef<Success<Paginated<TResponse[]>>> {
    return watchRequest(
      this.url,
      PaginatedSchema(this.responseSchema),
      filters,
    );
  }

  getOrder(id: string): ResultOkRef<Success<TResponse>> {
    return watchRequest(`${this.url}${id}/`, this.responseSchema);
  }

  getAllOrders(filters?: QueryParams): Promise<Result<TResponse[]>> {
    return api.get(this.url, this.responseSchema.array(), {
      ...filters,
      all: "true",
    });
  }

  createOrder(payload: TPayload): Promise<Result<TResponse>> {
    return api.post(
      this.url,
      this.responseSchema,
      payload as Record<string, unknown>,
    );
  }

  createBlikCheckoutSession(
    payload: TPayload,
    checkoutSessionId?: string,
  ): Promise<Result<CheckoutSessionResponse>> {
    return api.post(
      "/orders-client/checkout-session/",
      CheckoutSessionResponseSchema,
      {
        ...(payload as Record<string, unknown>),
        checkout_session_id: checkoutSessionId,
      },
    );
  }

  updateOrder(id: string, payload: TPayload): Promise<Result<TResponse>> {
    return api.patch(
      this.url + `${id}/`,
      this.responseSchema,
      payload as Record<string, unknown>,
    );
  }

  deleteOrder(id: string): Promise<Result<void>> {
    return api.delete(this.url + `${id}/`);
  }
}

function createClientOrderApi() {
  return new OrderApi<
    OrderPayload,
    OrderResponse,
    OrderDeliveryResponse,
    OrderPickupResponse
  >("/orders-client/", OrderResponseSchema);
}

class AdminOrderApi extends OrderApi<
  OrderAdminPayload,
  OrderAdminResponse,
  OrderDeliveryAdminResponse,
  OrderPickupAdminResponse
> {
  constructor() {
    super("/orders/", OrderAdminResponseSchema);
  }

  searchOrders(
    search: string,
    pageSize = 8,
  ): Promise<Result<Paginated<OrderAdminResponse[]>>> {
    return api.get(
      "/orders/",
      PaginatedSchema(OrderAdminResponseSchema),
      {
        search,
        page: 1,
        page_size: pageSize,
        ordering: "-created_at",
      },
    );
  }

  massUpdateOrders(
    payload: OrderMassUpdatePayload,
  ): Promise<Result<OrderMassUpdateResponse>> {
    return api.post(
      "/orders/mass-update/",
      OrderMassUpdateResponseSchema,
      payload,
    );
  }
}

function createAdminOrderApi() {
  return new AdminOrderApi();
}

const clientStore = defineStore("order-client", () => ({
  api: markRaw(createClientOrderApi()),
}));

const adminStore = defineStore("order-admin", () => ({
  api: markRaw(createAdminOrderApi()),
}));

export function useClientOrders() {
  return clientStore().api;
}

export function useAdminOrders() {
  return adminStore().api;
}
